import { useCallback, useSyncExternalStore } from 'react';

/**
 * The useMediaQuery hook leverages the window.matchMedia API to subscribe
 * to CSS media query changes, thereby providing real-time responsiveness to
 * dynamic changes in the viewport or screen orientation. It allows the
 * component to rerender whenever the media query’s result changes.
 *
 * It throws an error if attempted to be used on the server-side
 * (media queries only work in the browser).
 *
 * https://usehooks.com/usemediaquery
 *
 * @usage
 * const isLargeDevice = useMediaQuery("only screen and (min-width : 993px) and (max-width : 1200px)");
 *
 * @source
 * https://github.com/uidotdev/usehooks/blob/main/index.js
 */
export default function useMediaQuery(query: string) {
  const subscribe = useCallback(
    (callback) => {
      const matchMedia = window.matchMedia(query);

      matchMedia.addEventListener("change", callback);
      return () => {
        matchMedia.removeEventListener("change", callback);
      };
    },
    [query]
  );

  const getSnapshot = () => {
    return window.matchMedia(query).matches;
  };

  const getServerSnapshot = () => {
    throw Error("useMediaQuery is a client-only hook");
  };

  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}